import type { z } from 'zod';

import type { AssetType, Rule, TemplateName } from '../core/assetType.js';

import { dilOutcomesRouted } from '../rules/dil/outcomesRouted.js';
import { envelopeCoinValue } from '../rules/envelope/coinValue.js';
import { envelopeConsiderationAreaSlugs } from '../rules/envelope/considerationAreaSlugs.js';
import { envelopeConstants } from '../rules/envelope/constants.js';
import { envelopeFlatTaxonomy } from '../rules/envelope/flatTaxonomy.js';
import { envelopeIdsMatch } from '../rules/envelope/idsMatch.js';
import { envelopeIndexing } from '../rules/envelope/indexing.js';
import { envelopeInnerKey } from '../rules/envelope/innerKey.js';
import { envelopeInternalName } from '../rules/envelope/internalName.js';
import { envelopeMeta } from '../rules/envelope/meta.js';
import { envelopeSkills } from '../rules/envelope/skills.js';
import { envelopeStructuredLearningOutcome } from '../rules/envelope/structuredLearningOutcome.js';
import { activityAnswerIntegrity } from '../rules/gameshow/activityAnswerIntegrity.js';
import { characterDialogueNames } from '../rules/gameshow/characterDialogueNames.js';
import { characters as gameshowCharacters } from '../rules/gameshow/characters.js';
import { endingNode } from '../rules/gameshow/endingNode.js';
import { introFlow } from '../rules/gameshow/introFlow.js';
import { gameshowNarratorName } from '../rules/gameshow/narratorName.js';
import { gameshowNarratorSlug } from '../rules/gameshow/narratorSlug.js';
import { optionOutcomeEffects } from '../rules/gameshow/optionOutcomeEffects.js';
import { quizRounds } from '../rules/gameshow/quizRounds.js';
import { resultsNodes } from '../rules/gameshow/resultsNodes.js';
import { revealNodes } from '../rules/gameshow/revealNodes.js';
import { scoreDefinitions } from '../rules/gameshow/scoreDefinitions.js';
import { scoreDeltaForbiddenFields } from '../rules/gameshow/scoreDeltaForbiddenFields.js';
import { startNode } from '../rules/gameshow/startNode.js';
import { swipeSelectBanned } from '../rules/gameshow/swipeSelectBanned.js';
import { topLevelForbiddenFields } from '../rules/gameshow/topLevelForbiddenFields.js';
import { completeStateFlagForbidden } from '../rules/lesson/completeStateFlagForbidden.js';
import { linearStructure } from '../rules/lesson/linearStructure.js';
import { lessonNarratorName } from '../rules/lesson/narratorName.js';
import { noCharacters } from '../rules/lesson/noCharacters.js';
import { skillsArrayForbidden } from '../rules/lesson/skillsArrayForbidden.js';
import { teachTestLoops } from '../rules/lesson/teachTestLoops.js';
import { activities } from '../rules/shared/activities.js';
import { castUnique } from '../rules/shared/castUnique.js';
import { characterRequestMismatch, characterRequestPresent, characterRequestWellFormed } from '../rules/shared/characterRequest.js';
import { checklistEndingShape } from '../rules/shared/checklistEndingShape.js';
import { conditionsKnown } from '../rules/shared/conditionsKnown.js';
import { contentAssetSlugsKnown } from '../rules/shared/contentAssetSlugsKnown.js';
import { dragPresolved } from '../rules/shared/dragPresolved.js';
import { scoreRangesInBounds, startNodeKnown } from '../rules/shared/graph.js';
import { multiChoicePositionVariation } from '../rules/shared/multiChoicePositionVariation.js';
import { richEndingShape } from '../rules/shared/richEndingShape.js';
import { scoreRoutes } from '../rules/shared/scoreRoutes.js';
import { activityNodeShape } from '../rules/story/activityNodeShape.js';
import { activityTypesNoRepeat } from '../rules/story/activityTypesNoRepeat.js';
import { charactersUnique } from '../rules/story/charactersUnique.js';
import { contentAssetSlugPresent } from '../rules/story/contentAssetSlugPresent.js';
import { dialogueLengthLimits } from '../rules/story/dialogueLengthLimits.js';
import { endingExitRoutesForbidden } from '../rules/story/endingExitRoutesForbidden.js';
import { endingNodeShape } from '../rules/story/endingNodeShape.js';
import { exitRouteTargets } from '../rules/story/exitRouteTargets.js';
import { legacySkillsArray } from '../rules/story/legacySkillsArray.js';
import { nodesUnique } from '../rules/story/nodesUnique.js';
import { outcomesMatchExitRoutes } from '../rules/story/outcomesMatchExitRoutes.js';
import { reachability, reachabilityAt } from '../rules/story/reachability.js';
import { swipeSelectFinishesComplete } from '../rules/story/swipeSelectFinishesComplete.js';
import { Dil } from './dil.js';
import { makeEnvelopeSchema } from './envelope.js';
import { makeEnvelopeAssetType } from './envelopeAssetType.js';
import { Gameshow } from './gameshow.js';
import { Lesson } from './lesson.js';
import { Story } from './story.js';

/** The full envelope-level rule set, shared by every learning-asset template. */
export const envelopeRules = (templateName: TemplateName): Rule<unknown>[] => [
  envelopeCoinValue(templateName),
  envelopeConstants(templateName),
  envelopeIdsMatch(templateName),
  envelopeInnerKey(templateName),
  envelopeSkills(templateName),
  envelopeFlatTaxonomy(templateName),
  envelopeMeta(templateName),
  envelopeStructuredLearningOutcome(templateName),
  envelopeInternalName(templateName),
  envelopeIndexing(templateName),
  envelopeConsiderationAreaSlugs(templateName),
];

/**
 * The inner rules every template runs against its `storyData`. Each is inert when the
 * mechanism it checks is absent.
 */
const sharedInnerRules = (templateName: TemplateName): Rule<unknown>[] => [
  activities(templateName) as Rule<unknown>,
  contentAssetSlugsKnown(templateName),
  characterRequestPresent(templateName),
  characterRequestWellFormed(templateName),
  characterRequestMismatch(templateName),
  castUnique,
  scoreRoutes,
  conditionsKnown,
  scoreRangesInBounds,
  richEndingShape,
  checklistEndingShape,
  startNodeKnown,
  ...(templateName === 'gameshow'
    ? []
    : [
        dragPresolved(templateName) as Rule<unknown>,
        multiChoicePositionVariation(templateName) as Rule<unknown>,
      ]),
];

/** A learning-asset template type: the envelope schema around its inner schema and rules. */
export const makeLearningAssetType = <TInner>(
  templateName: TemplateName,
  innerSchema: z.ZodType<TInner>,
  innerRules: Rule<TInner>[] = [],
): AssetType<unknown> =>
  makeEnvelopeAssetType<TInner>({
    templateName,
    envelopeSchema: makeEnvelopeSchema(innerSchema, templateName),
    envelopeRules: envelopeRules(templateName),
    innerRules: [...(sharedInnerRules(templateName) as Rule<TInner>[]), ...innerRules],
  });

const storyRules: Rule<Story>[] = [
  legacySkillsArray,
  contentAssetSlugPresent,
  reachability,
  charactersUnique,
  nodesUnique,
  activityNodeShape,
  endingNodeShape,
  exitRouteTargets,
  endingExitRoutesForbidden,
  outcomesMatchExitRoutes,
  swipeSelectFinishesComplete,
  activityTypesNoRepeat,
  dialogueLengthLimits,
];

const lessonRules: Rule<Lesson>[] = [
  skillsArrayForbidden,
  completeStateFlagForbidden,
  lessonNarratorName,
  linearStructure,
  noCharacters,
  teachTestLoops,
  swipeSelectFinishesComplete,
];

/** Runs a story graph rule only on a document that has a graph: a start and a node list. */
const onGraph = <T>(rule: Rule<Story>): Rule<T> => ({
  code: rule.code,
  check: (input, ctx) => {
    const graph = input as { startNodeId?: unknown; nodes?: unknown };
    return typeof graph.startNodeId === 'string' && Array.isArray(graph.nodes) ? rule.check(graph as Story, ctx) : [];
  },
});

const gameshowRules: Rule<Gameshow>[] = [
  gameshowNarratorName,
  gameshowNarratorSlug,
  topLevelForbiddenFields,
  scoreDeltaForbiddenFields,
  startNode,
  onGraph(nodesUnique),
  onGraph(exitRouteTargets),
  onGraph(reachabilityAt('warning')),
  gameshowCharacters,
  characterDialogueNames,
  scoreDefinitions,
  introFlow,
  quizRounds,
  revealNodes,
  resultsNodes,
  endingNode,
  optionOutcomeEffects,
  activityAnswerIntegrity,
  swipeSelectBanned,
];

/** A day-in-the-life branches and merges its own way, but every route has to land and every outcome needs one. */
const dilRules: Rule<Dil>[] = [onGraph(exitRouteTargets), onGraph(reachability), onGraph(dilOutcomesRouted), swipeSelectFinishesComplete];

/** The default registry contents: all four learning-asset templates. */
export const defaultAssetTypes = (): AssetType<unknown>[] => [
  makeLearningAssetType('story', Story, storyRules),
  makeLearningAssetType('lesson', Lesson, lessonRules),
  makeLearningAssetType('gameshow', Gameshow, gameshowRules),
  makeLearningAssetType('dil', Dil, dilRules),
];
